interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  ariaLabel: string;
  color?: string;
  className?: string;
}

export function Sparkline({
  values,
  width = 96,
  height = 24,
  ariaLabel,
  color = 'hsl(var(--primary))',
  className,
}: SparklineProps) {
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  // 1px inset top/bottom so the endpoint dot and rules don't clip.
  const pad = 2;
  const innerH = height - pad * 2;
  const stepX = width / (values.length - 1);

  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const y = pad + innerH - ((v - min) / span) * innerH;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  const lastIdx = values.length - 1;
  const lastX = lastIdx * stepX;
  const lastY = pad + innerH - ((values[lastIdx] - min) / span) * innerH;

  return (
    <svg
      role='img'
      aria-label={ariaLabel}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio='none'
      className={className}
    >
      {/* min/max rules — faint scaffolding so the reader can ground the shape */}
      <line
        x1={0}
        y1={pad}
        x2={width}
        y2={pad}
        stroke='hsl(var(--border))'
        strokeWidth={1}
        vectorEffect='non-scaling-stroke'
      />
      <line
        x1={0}
        y1={height - pad}
        x2={width}
        y2={height - pad}
        stroke='hsl(var(--border))'
        strokeWidth={1}
        vectorEffect='non-scaling-stroke'
      />
      <polyline
        fill='none'
        stroke={color}
        strokeWidth={1.4}
        strokeLinejoin='round'
        strokeLinecap='round'
        vectorEffect='non-scaling-stroke'
        points={points}
      />
      <path
        d={`M${lastX.toFixed(2)},${lastY.toFixed(2)}h0`}
        stroke='currentColor'
        strokeWidth={4}
        strokeLinecap='round'
        vectorEffect='non-scaling-stroke'
        fill='none'
      />
    </svg>
  );
}
